import { useCallback, useMemo, useState } from 'react';
import { RowAction } from '../types';
import { buildRequestFromRecord } from '../utils';

export const useRowModalHandler = <T extends Record<string, any>>(rowKey?: string) => {
	const [isOpen, setIsOpen] = useState(false);
	const [action, setAction] = useState<RowAction | null>(null);
	const [selectedRecords, setSelectedRecords] = useState<T[]>([]);

	const open = useCallback((action: RowAction, records: T[] = []) => {
		setAction(action);
		setIsOpen(true);
		setSelectedRecords(records);
	}, []);

	const close = useCallback(() => {
		setIsOpen(false);
	}, []);

	// Call once the exit animation finishes (AnimatePresence onExitComplete), not on close().
	const reset = useCallback(() => {
		setAction(null);
		setSelectedRecords([]);
	}, []);

	const contextData = useMemo(() => {
		if (!action || !action.isMultiple) return {};
		const key = action.identifiersKey ? action.identifiersKey : rowKey;
		if (!key) return {};
		return { identifiers: selectedRecords.map((r) => r[key as keyof T]) };
	}, [action, selectedRecords, rowKey]);

	const request = action
		? buildRequestFromRecord({
				action,
				selectedRecords,
				rowKey,
			})
		: null;

	return {
		isOpen,
		action,
		selectedRecords,
		open,
		close,
		reset,
		request,
		setSelectedRecords,
		contextData,
	};
};
